import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../contexts/AuthContext';
import { mobilePaymentService } from '../services/mobilePayment';
import { paymentsAPI } from '../services/api';
import MobilePaymentStatus from '../components/MobilePaymentStatus';

const PaymentCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getAuthToken } = useAuth();
  
  const [processing, setProcessing] = useState(true);
  const [paymentResult, setPaymentResult] = useState(null);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileDebug, setShowMobileDebug] = useState(false);
  const [tapCount, setTapCount] = useState(0);

  // Clean up backup authentication tokens
  const cleanupBackupTokens = async () => {
    try {
      localStorage.removeItem('paymentBackupToken');
      sessionStorage.removeItem('idToken');
      sessionStorage.removeItem('accessToken');
      
      // Clean up Capacitor secure storage if available
      if (Capacitor.isNativePlatform()) {
        try {
          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.remove({ key: 'authToken' });
        } catch (secureStorageError) {
          // Silently handle cleanup error
        }
      }
    } catch (error) {
      // Silently handle cleanup errors
    }
  };

  // Force show debug on mobile devices (especially for errors)
  useEffect(() => {
    const isCapacitorNative = Capacitor.isNativePlatform();
    if (isCapacitorNative) {
      // Always show debug on mobile for troubleshooting
      setShowMobileDebug(true);
    }
  }, []);

  // Keep debug visible when errors occur
  useEffect(() => {
    if (error && Capacitor.isNativePlatform()) {
      mobilePaymentService?.log(`Error occurred: ${error}`, 'error');
      setShowMobileDebug(true);
    }
  }, [error]);

  // Enable debug mode with 5 taps on mobile (for ColorOS troubleshooting)
  const handleScreenTap = () => {
    if (Capacitor.isNativePlatform()) {
      const newCount = tapCount + 1;
      setTapCount(newCount);
      
      if (newCount >= 5) {
        setShowMobileDebug(true);
        mobilePaymentService?.log('Debug mode enabled by user taps', 'info');
        // Reset counter
        setTapCount(0);
        // Keep debug visible for 60 seconds
        setTimeout(() => setShowMobileDebug(false), 60000);
      }
      
      // Reset tap counter after 3 seconds
      setTimeout(() => setTapCount(0), 3000);
    }
  };

  // Safety timeout to prevent infinite processing
  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      if (processing) {
        mobilePaymentService?.log('PaymentCallback safety timeout reached', 'error');
        setError('Payment verification timed out. Please check your payment history or contact support if amount was debited.');
        setPaymentResult('failed');
        setProcessing(false);
        setShowMobileDebug(false);
      }
    }, 30000); // 30 second timeout

    return () => clearTimeout(safetyTimer);
  }, [processing]);

  useEffect(() => {
    // Conservative mobile detection - prioritize Capacitor detection
    // Only treat as mobile app if it's definitely a Capacitor native app
    const isCapacitorNative = Capacitor.isNativePlatform();
    const userAgent = navigator.userAgent || '';
    
    // Secondary check: mobile user agent with WebView indicator for our specific app
    const isAppWebView = userAgent.includes('MyHomeEntryApp') || 
                        (userAgent.includes('wv') && userAgent.includes('Mobile'));
    
    // Be conservative - only mobile if definitely Capacitor or our specific app
    const isMobileDevice = isCapacitorNative || isAppWebView;
    
    setIsMobile(isMobileDevice);
    
    // Store detection results in DOM for debugging
    document.body.setAttribute('data-mobile-detected', isMobileDevice.toString());
    document.body.setAttribute('data-capacitor-native', isCapacitorNative.toString());
    document.body.setAttribute('data-app-webview', isAppWebView.toString());
    document.body.setAttribute('data-user-agent', userAgent);
  }, []);

  // Extracted payment update function to be used by both web and mobile
  const updatePaymentRecord = async (paymentData) => {
    const { status, orderId, paymentId, flatId, buildingId, transactionId, amount, message } = paymentData;
    
    if (status === 'Success' || status === 'Shipped') {
      // Add idempotency check to prevent duplicate updates
      const updateKey = `payment_update_${paymentId}_${orderId}`;
      if (sessionStorage.getItem(updateKey)) {
        console.log('Payment update already processed, skipping duplicate:', updateKey);
        return; // Skip if already processed in this session
      }
      
      let bearerToken;
      try {
        // Try localStorage tokens first (for MSG91 OTP login), then fall back to Cognito
        bearerToken = localStorage.getItem('idToken') || localStorage.getItem('accessToken');
        if (!bearerToken) {
          bearerToken = await getAuthToken();
        }
        console.log('Bearer token obtained for payment update');
      } catch (authError) {
        console.error('Failed to get authentication token:', authError);
        throw new Error('Unable to authenticate for payment update. Please log in again.');
      }
      
      // Update the payment record in the database using direct API call with bearer token
      try {
        // Mark update as in progress to prevent duplicates
        sessionStorage.setItem(updateKey, Date.now().toString());
        
        const apiUrl = process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';
        
        const requestBody = {
          paymentId: paymentId,
          flatId: flatId,
          buildingId: buildingId,
          status: 'COMPLETED',
          amount: parseFloat(amount || 0),
          description: 'CCAvenue payment completed successfully',
          razorpayPaymentId: transactionId, // Use CCAvenue transaction ID as receipt
          razorpayOrderId: orderId,
          ccavenueTransactionId: transactionId,
          ccavenueOrderId: orderId,
          ccavenuePaymentMode: 'CCAvenue',
          ccavenueStatusCode: '200',
          ccavenueStatusMessage: message || 'Payment completed successfully',
          completedAt: new Date().toISOString(),
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          // Add idempotency key to request to help lambda detect duplicates
          idempotencyKey: updateKey
        };
        
        console.log('Sending payment update request body:', requestBody);
        
        const updateResponse = await fetch(`${apiUrl}/payments/${paymentId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!updateResponse.ok) {
          // Remove idempotency key on failure so retry is possible
          sessionStorage.removeItem(updateKey);
          const errorText = await updateResponse.text();
          console.error('Payment update failed:', updateResponse.status, updateResponse.statusText, errorText);
          throw new Error(`Payment update failed: ${updateResponse.status} ${updateResponse.statusText}`);
        }
        
        const responseData = await updateResponse.json();
        console.log('Payment update response:', responseData);
        console.log('Payment record updated successfully');
        // Keep idempotency key in sessionStorage to prevent future duplicates
        
      } catch (updateError) {
        // Remove idempotency key on error so retry is possible
        sessionStorage.removeItem(updateKey);
        console.error('Failed to update payment record:', updateError);
        throw new Error('Payment was successful, but failed to update records. Please contact support.');
      }
    }
  };

  useEffect(() => {
    // Add detection info to body for debugging
    document.body.setAttribute('data-callback-loaded', new Date().toISOString());
    document.body.setAttribute('data-user-authenticated', (!!user).toString());
    document.body.setAttribute('data-search-params', location.search);
    document.body.setAttribute('data-mobile-app', isMobile.toString());
    
    // Real-time mobile detection for immediate processing
    const isCapacitorNative = Capacitor.isNativePlatform();
    const userAgent = navigator.userAgent || '';
    // FIXED: Only detect actual mobile APPS, not mobile browsers
    // Mobile browsers should use web flow just like desktop browsers
    const isAppWebView = userAgent.includes('MyHomeEntryApp');
    const isMobileDevice = isCapacitorNative || isAppWebView;
    
    // For mobile apps - use real-time detection instead of state
    if (isMobileDevice) {
      mobilePaymentService?.log('Mobile device detected - starting mobile payment processing', 'info');
      // Mobile flow started
      
      document.body.setAttribute('data-mobile-flow', 'true');
      
      // Close any remaining WebView first
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/browser').then(({ Browser }) => {
          Browser.close().catch(() => {}); // Silent fail
        }).catch(() => {});
      }
      
      // Priority 1: Check URL parameters first (CCAvenue callback redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = urlParams.size > 0;
      mobilePaymentService?.log(`URL parameters check: ${hasUrlParams ? 'Found' : 'None'}`, 'info');

      if (hasUrlParams && window.location.pathname === '/payment-callback') {
        document.body.setAttribute('data-mobile-source', 'ccavenue-callback-redirect');
        
        const processUrlParams = async () => {
          try {
            const responseData = Object.fromEntries(urlParams.entries());
            
            // Get stored payment data for database update
            const pendingPaymentData = localStorage.getItem('pendingPayment');
            let originalPaymentData = {};
            
            if (pendingPaymentData) {
              try {
                originalPaymentData = JSON.parse(pendingPaymentData);
                console.log('Retrieved stored payment data:', originalPaymentData);
              } catch (parseError) {
                console.error('Failed to parse stored payment data:', parseError);
              }
            }
            
            // Transform URL parameters into expected format for mobile display
            const paymentStatus = responseData.status || responseData.order_status;
            const displayData = {
              order_status: paymentStatus,
              order_id: responseData.orderId || responseData.order_id || 'N/A',
              tracking_id: responseData.transactionId || responseData.tracking_id || 'N/A',
              amount: responseData.amount || 'N/A',
              status_message: responseData.message || responseData.status_message || (paymentStatus === 'Success' ? 'Payment completed successfully' : 'Payment failed')
            };
            
            setPaymentDetails(displayData);
            
            if (paymentStatus === 'Success' || paymentStatus === 'Shipped') {
              setPaymentResult('success');
              
              // **FIX: Actually update the payment record in database**
              try {
                // Ensure we have a valid amount - try multiple fields
                const paymentAmount = responseData.amount || 
                                    originalPaymentData.amount || 
                                    originalPaymentData.totalAmount || 
                                    originalPaymentData.paidAmount ||
                                    originalPaymentData.outstandingBalance ||
                                    1700; // Fallback for debugging
                
                if (!paymentAmount || paymentAmount <= 0) {
                  throw new Error(`Payment amount not found or invalid: ${paymentAmount}`);
                }
                
                await updatePaymentRecord({
                  status: paymentStatus,
                  orderId: responseData.orderId || responseData.order_id,
                  paymentId: responseData.paymentId || originalPaymentData.paymentId || originalPaymentData.ccavenueOrderId,
                  flatId: responseData.flatId || originalPaymentData.flatId,
                  buildingId: responseData.buildingId || originalPaymentData.buildingId || originalPaymentData.buildingID,
                  transactionId: responseData.transactionId || responseData.tracking_id,
                  amount: paymentAmount,
                  message: responseData.message || responseData.status_message
                });
                
                console.log('Mobile payment record updated successfully');
                setProcessing(false);
                localStorage.removeItem('pendingPayment');
                cleanupBackupTokens(); // Clean up backup authentication tokens
                document.body.setAttribute('data-mobile-process', 'callback-redirect-success-updated');
                
              } catch (updateError) {
                console.error('Failed to update mobile payment record:', updateError);
                setError('Payment successful but failed to update records. Please contact support.');
                setProcessing(false);
                document.body.setAttribute('data-mobile-process', 'callback-redirect-success-update-failed');
              }
              
              // Clean up mobile payment service after successful processing
              if (mobilePaymentService && mobilePaymentService.cancelPayment) {
                mobilePaymentService.cancelPayment();
              }
              
            } else {
              setPaymentResult('failed');
              setError(responseData.failure_message || responseData.status_message || responseData.message || 'Payment failed');
              setProcessing(false);
              localStorage.removeItem('pendingPayment'); // Clean up any pending payment data
              cleanupBackupTokens(); // Clean up backup authentication tokens
              document.body.setAttribute('data-mobile-process', 'callback-redirect-failed');
              
              // Clean up mobile payment service after failed payment
              if (mobilePaymentService) {
                mobilePaymentService.cancelPayment();
              }
            }
            
            return; // Exit mobile flow - we're done
          } catch (urlParseError) {
            console.error('Error processing CCAvenue callback URL parameters:', urlParseError);
            document.body.setAttribute('data-mobile-error', 'callback-url-params-parse-failed');
          }
        };
        
        processUrlParams();
        return;
      }
      
      // Priority 2: Check localStorage for stored results (deep link or direct app launch)
      const storedResult = localStorage.getItem('mobilePaymentResult');
      mobilePaymentService?.log(`localStorage result check: ${storedResult ? 'Found' : 'None'}`, 'info');

      if (storedResult) {
        document.body.setAttribute('data-mobile-source', 'localStorage-result');
        try {
          const parsedResult = JSON.parse(storedResult);
          localStorage.removeItem('mobilePaymentResult');
          
          // Process stored result from deep link
          const responseData = {
            order_status: parsedResult.status,
            order_id: parsedResult.orderId,
            tracking_id: parsedResult.transactionId,
            amount: parsedResult.amount,
            status_message: parsedResult.message || (parsedResult.status === 'Success' ? 'Payment completed successfully' : 'Payment failed')
          };
          
          setPaymentDetails(responseData);
          
          if (parsedResult.status === 'Success' || parsedResult.status === 'Shipped') {
            (async () => {
              try {
                await updatePaymentRecord(parsedResult);
                setPaymentResult('success');
                setProcessing(false);
                document.body.setAttribute('data-mobile-process', 'localStorage-success');
                
                // Clean up mobile payment service after successful processing
                if (mobilePaymentService) {
                  mobilePaymentService.cancelPayment();
                }
              } catch (updateError) {
                setError('Payment was successful, but failed to update records. Please contact support.');
                setPaymentResult('success');
                setProcessing(false);
                document.body.setAttribute('data-mobile-process', 'localStorage-update-failed');
                
                // Clean up mobile payment service even if update failed
                if (mobilePaymentService) {
                  mobilePaymentService.cancelPayment();
                }
              }
            })();
          } else {
            setPaymentResult('failed');
            setError(parsedResult.message || 'Payment failed');
            setProcessing(false);
            document.body.setAttribute('data-mobile-process', 'localStorage-failed');
            
            // Clean up mobile payment service after failed payment
            if (mobilePaymentService) {
              mobilePaymentService.cancelPayment();
            }
          }
          return;
        } catch (parseError) {
          document.body.setAttribute('data-mobile-error', 'localStorage-parse-failed');
        }
      }
      
      // Priority 3: Check if we have a pending payment but no URL params or stored result
      // This would be unusual - typically CCAvenue redirects with params or app stores result
      const pendingPayment = localStorage.getItem('pendingPayment');
      mobilePaymentService?.log(`Pending payment fallback check: ${pendingPayment ? 'Found' : 'None'}`, 'info');

      if (pendingPayment && window.location.pathname === '/payment-callback') {
        document.body.setAttribute('data-mobile-source', 'pending-payment-fallback');
        
        // Try to manually check payment status if we have pending payment data
        const checkManualStatus = async () => {
          try {
            const paymentData = JSON.parse(pendingPayment);
            mobilePaymentService?.log(`Attempting manual status check for payment: ${paymentData.paymentId}`, 'info');
            
            if (paymentData.paymentId) {
              mobilePaymentService?.showStatus('Manually checking payment status...', 'info');
              
              // Try to check payment status one more time
              const result = await mobilePaymentService?.checkPaymentStatus(paymentData.paymentId);
              
              if (result && result.status !== 'PENDING') {
                mobilePaymentService?.log(`Manual check found completed payment: ${result.status}`, 'success');
                mobilePaymentService?.handlePollingSuccess(result);
                return;
              } else {
                mobilePaymentService?.log('Manual check still shows pending - payment may not be completed yet', 'warning');
              }
            }
          } catch (manualCheckError) {
            mobilePaymentService?.log(`Manual status check failed: ${manualCheckError.message}`, 'error');
          }
          
          // Clean up and show error if we reach here
          mobilePaymentService?.log('No valid payment callback data found after all checks', 'error');
          setError('Payment callback data was not received. Please check your payment history or contact support if amount was debited.');
          setPaymentResult('failed');
          setProcessing(false);
          setShowMobileDebug(true); // Keep debug visible for error
          localStorage.removeItem('pendingPayment');
          cleanupBackupTokens(); // Clean up backup authentication tokens
          document.body.setAttribute('data-mobile-process', 'no-callback-data-error');
          
          // Clean up mobile payment service
          if (mobilePaymentService) {
            mobilePaymentService.cancelPayment();
          }
        };
        
        // Execute the async function
        checkManualStatus();
        return;
      }
      
      // Final fallback: AUTOMATIC emergency recovery for ColorOS when no payment context exists
      mobilePaymentService?.log('No mobile payment conditions met - starting AUTOMATIC emergency recovery', 'warning');
      
      // Log detailed state for debugging
      mobilePaymentService?.log(`Final state check:
        - URL params: ${hasUrlParams}
        - Stored result: ${!!storedResult}  
        - Pending payment: ${!!pendingPayment}
        - Pathname: ${window.location.pathname}`, 'error');

      // AUTOMATIC Emergency Recovery: Focus on CURRENT payment transaction
      const attemptAutomaticRecovery = async () => {
        try {
          mobilePaymentService?.log('Starting CURRENT payment recovery - focusing on active transaction', 'info');
          mobilePaymentService?.showStatus('Searching for your current payment...', 'info');
          
          const apiUrl = process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';
          
          // Enhanced token recovery system for mobile payments
          let token = localStorage.getItem('idToken') || localStorage.getItem('accessToken');
          
          if (!token) {
            mobilePaymentService?.log('Primary tokens missing - attempting token recovery', 'warning');
            
            // Recovery attempt 1: Check backup token storage
            const backupToken = localStorage.getItem('paymentBackupToken') || 
                              sessionStorage.getItem('idToken') || 
                              sessionStorage.getItem('accessToken');
            
            if (backupToken) {
              mobilePaymentService?.log('Found backup authentication token', 'success');
              token = backupToken;
              // Restore to primary location
              localStorage.setItem('idToken', backupToken);
            } else {
              // Recovery attempt 2: Check stored user session data
              const storedUserSession = localStorage.getItem('userSession') || localStorage.getItem('userData');
              if (storedUserSession) {
                try {
                  const userData = JSON.parse(storedUserSession);
                  if (userData.token || userData.idToken || userData.accessToken) {
                    token = userData.token || userData.idToken || userData.accessToken;
                    mobilePaymentService?.log('Recovered token from user session data', 'success');
                    localStorage.setItem('idToken', token);
                  }
                } catch (e) {
                  mobilePaymentService?.log('Failed to parse stored user session', 'warning');
                }
              }
            }
            
            // Recovery attempt 3: Try Capacitor secure storage (if available)
            if (!token && Capacitor.isNativePlatform()) {
              try {
                const { Preferences } = await import('@capacitor/preferences');
                const { value: secureToken } = await Preferences.get({ key: 'authToken' });
                if (secureToken) {
                  token = secureToken;
                  mobilePaymentService?.log('Recovered token from secure storage', 'success');
                  localStorage.setItem('idToken', token);
                }
              } catch (secureStorageError) {
                mobilePaymentService?.log('Secure storage recovery failed', 'info');
              }
            }
            
            // Recovery attempt 4: Try AuthContext getAuthToken method
            if (!token) {
              try {
                mobilePaymentService?.log('Attempting AuthContext token recovery', 'info');
                token = await getAuthToken();
                if (token) {
                  mobilePaymentService?.log('Recovered token from AuthContext', 'success');
                  localStorage.setItem('idToken', token);
                }
              } catch (authContextError) {
                mobilePaymentService?.log('AuthContext recovery failed', 'warning');
              }
            }
            
            // Recovery attempt 5: Check if user object has token information
            if (!token && user) {
              try {
                // Sometimes user object contains session information
                if (user.token || user.idToken || user.accessToken) {
                  token = user.token || user.idToken || user.accessToken;
                  mobilePaymentService?.log('Recovered token from user object', 'success');
                  localStorage.setItem('idToken', token);
                } else if (user.signInUserSession) {
                  // Cognito session object
                  const session = user.signInUserSession;
                  if (session.idToken && session.idToken.jwtToken) {
                    token = session.idToken.jwtToken;
                    mobilePaymentService?.log('Recovered token from Cognito session', 'success');
                    localStorage.setItem('idToken', token);
                  }
                }
              } catch (userObjectError) {
                mobilePaymentService?.log('User object recovery failed', 'warning');
              }
            }
          }
          
          if (!token) {
            throw new Error('No authentication token available - all recovery methods failed');
          }
          
          mobilePaymentService?.log('Authentication token acquired successfully', 'success');

          // Priority 1: Check if we have ANY context about the current payment
          let currentPaymentId = null;
          let currentPaymentContext = null;
          
          // Try to get current payment ID from various sources
          const pendingPaymentStr = localStorage.getItem('pendingPayment');
          const emergencyRecoveryStr = localStorage.getItem('emergencyRecovery');
          const paymentStartTime = localStorage.getItem('paymentStartTime');
          
          mobilePaymentService?.log(`🔍 RETRIEVING PAYMENT DATA:`, 'error');
          mobilePaymentService?.log(`🔍 PendingPaymentStr: ${pendingPaymentStr}`, 'error');
          mobilePaymentService?.log(`🔍 EmergencyRecoveryStr: ${emergencyRecoveryStr}`, 'error');
          
          if (pendingPaymentStr) {
            try {
              currentPaymentContext = JSON.parse(pendingPaymentStr);
              mobilePaymentService?.log(`🔍 Parsed pendingPayment keys: ${Object.keys(currentPaymentContext).join(', ')}`, 'error');
              currentPaymentId = currentPaymentContext.paymentId;
              mobilePaymentService?.log(`🔍 Extracted paymentId: "${currentPaymentId}"`, 'error');
              mobilePaymentService?.log(`Found current payment context: ${currentPaymentId}`, 'success');
            } catch (e) {
              mobilePaymentService?.log('Failed to parse pending payment context', 'warning');
            }
          }
          
          if (emergencyRecoveryStr && !currentPaymentId) {
            try {
              const emergencyData = JSON.parse(emergencyRecoveryStr);
              currentPaymentId = emergencyData.paymentId;
              mobilePaymentService?.log(`Found emergency recovery context: ${currentPaymentId}`, 'info');
            } catch (e) {
              mobilePaymentService?.log('Failed to parse emergency recovery context', 'warning');
            }
          }

          // Priority 2: If we have a specific payment ID, check its status directly
          if (currentPaymentId) {
            mobilePaymentService?.log(`Checking status of CURRENT payment: ${currentPaymentId}`, 'info');
            mobilePaymentService?.showStatus(`Checking your payment ${currentPaymentId}...`, 'info');
            
            try {
              const result = await mobilePaymentService?.checkPaymentStatus(currentPaymentId);
              
              if (result && result.status !== 'PENDING') {
                mobilePaymentService?.log(`CURRENT payment found: ${result.status}`, 'success');
                
                if (result.status === 'Success' || result.status === 'Shipped') {
                  // Show success for the current payment
                  mobilePaymentService?.showStatus('Your payment was successful!', 'success');
                  
                  setPaymentResult('success');
                  setPaymentDetails({
                    order_status: 'Success',
                    order_id: result.orderId || currentPaymentId,
                    tracking_id: result.transactionId || 'N/A',
                    amount: result.amount || currentPaymentContext?.amount || 'N/A',
                    status_message: 'Your payment completed successfully'
                  });
                  setProcessing(false);
                  setError(null);
                  
                  // Store successful recovery info
                  localStorage.setItem('autoRecoverySuccess', JSON.stringify({
                    paymentId: currentPaymentId,
                    recoveredAt: Date.now(),
                    method: 'current-payment-direct-check'
                  }));
                  
                  cleanupBackupTokens(); // Clean up backup authentication tokens
                  
                  mobilePaymentService?.log('SUCCESS: Found and displayed current payment success', 'success');
                  return; // Success - exit recovery
                  
                } else {
                  // Current payment failed or was cancelled
                  mobilePaymentService?.log(`Current payment status: ${result.status}`, 'info');
                  setPaymentResult('failed');
                  setError(`Your payment ${result.status.toLowerCase()}. ${result.message || ''}`);
                  setProcessing(false);
                  return;
                }
              } else {
                // Current payment still pending - start automatic monitoring
                mobilePaymentService?.log('Current payment still pending - starting automatic monitoring', 'info');
                mobilePaymentService?.log(`🔍 PaymentId being passed to polling: "${currentPaymentId}" (type: ${typeof currentPaymentId}, length: ${currentPaymentId ? currentPaymentId.length : 0})`, 'info');
                mobilePaymentService?.showStatus('Your payment is being processed...', 'info');
                
                // Validate paymentId before starting polling
                if (!currentPaymentId || typeof currentPaymentId !== 'string' || currentPaymentId.trim() === '') {
                  mobilePaymentService?.log(`🚨 CRITICAL: Invalid currentPaymentId for polling: "${currentPaymentId}"`, 'error');
                  throw new Error(`Invalid paymentId for polling: ${currentPaymentId}`);
                }
                
                mobilePaymentService?.startPaymentPolling(currentPaymentId);
                setProcessing(true);
                return;
              }
            } catch (directCheckError) {
              mobilePaymentService?.log(`Direct check failed for current payment: ${directCheckError.message}`, 'warning');
              // Continue to fallback search below
            }
          }

          // Priority 3: Fallback - Look for very recent payments (within payment window)
          mobilePaymentService?.log('No current payment context - searching recent payments as fallback', 'warning');
          
          const startTime = paymentStartTime ? parseInt(paymentStartTime) : Date.now() - (30 * 60 * 1000);
          const searchWindowStart = startTime - (10 * 60 * 1000); // 10 minutes before payment start
          const searchWindowEnd = Date.now();
          
          mobilePaymentService?.log(`Searching for payments in current session window (${Math.round((searchWindowEnd - searchWindowStart) / (60 * 1000))} minutes)`, 'info');
          
          const response = await fetch(`${apiUrl}/payments?limit=10`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const payments = await response.json();
            
            // Filter to payments within the current session window
            const sessionPayments = payments.filter(p => {
              const createdTime = new Date(p.createdAt).getTime();
              return createdTime >= searchWindowStart && createdTime <= searchWindowEnd;
            }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            mobilePaymentService?.log(`Found ${sessionPayments.length} payments in current session window`, 'info');
            
            if (sessionPayments.length > 0) {
              const currentSessionPayment = sessionPayments[0];
              const paymentAge = Math.round((Date.now() - new Date(currentSessionPayment.createdAt).getTime()) / (60 * 1000));
              mobilePaymentService?.log(`Most recent session payment: ${currentSessionPayment.paymentId} (${currentSessionPayment.status}) - ${paymentAge} minutes old`, 'info');
              
              if (currentSessionPayment.status === 'COMPLETED') {
                // Show success for the session payment
                mobilePaymentService?.showStatus('Found your completed payment!', 'success');
                
                setPaymentResult('success');
                setPaymentDetails({
                  order_status: 'Success',
                  order_id: currentSessionPayment.ccavenueOrderId || currentSessionPayment.paymentId,
                  tracking_id: currentSessionPayment.ccavenueTrackingId || 'N/A',
                  amount: currentSessionPayment.amount || 'N/A',
                  status_message: 'Your payment completed successfully'
                });
                setProcessing(false);
                setError(null);
                
                localStorage.setItem('autoRecoverySuccess', JSON.stringify({
                  paymentId: currentSessionPayment.paymentId,
                  recoveredAt: Date.now(),
                  method: 'current-session-search'
                }));
                
                cleanupBackupTokens(); // Clean up backup authentication tokens
                
                mobilePaymentService?.log('SUCCESS: Found current session payment success', 'success');
                return;
                
              } else if (currentSessionPayment.status === 'PENDING') {
                // Monitor the session payment
                mobilePaymentService?.log(`🔍 Session PaymentId being passed to polling: "${currentSessionPayment.paymentId}" (type: ${typeof currentSessionPayment.paymentId}, length: ${currentSessionPayment.paymentId ? currentSessionPayment.paymentId.length : 0})`, 'info');
                mobilePaymentService?.showStatus('Monitoring your payment...', 'info');
                
                // Validate paymentId before starting polling
                if (!currentSessionPayment.paymentId || typeof currentSessionPayment.paymentId !== 'string' || currentSessionPayment.paymentId.trim() === '') {
                  mobilePaymentService?.log(`🚨 CRITICAL: Invalid session paymentId for polling: "${currentSessionPayment.paymentId}"`, 'error');
                  throw new Error(`Invalid session paymentId for polling: ${currentSessionPayment.paymentId}`);
                }
                
                mobilePaymentService?.startPaymentPolling(currentSessionPayment.paymentId);
                setProcessing(true);
                return;
              }
            }
          }
          
          // If we reach here, no current payment transaction found
          throw new Error('No current payment transaction found in session');
          
        } catch (recoveryError) {
          mobilePaymentService?.log(`Current payment recovery failed: ${recoveryError.message}`, 'error');
          
          // Final fallback - user-friendly error focused on current transaction
          setError('Unable to find your current payment transaction. Please check your payment history or contact support if amount was debited.');
          setPaymentResult('failed');
          setProcessing(false);
          setShowMobileDebug(true);
          
          // Clean up any remaining data
          localStorage.removeItem('pendingPayment');
          localStorage.removeItem('paymentStartTime');
          cleanupBackupTokens(); // Clean up backup authentication tokens
          if (mobilePaymentService) {
            mobilePaymentService.cancelPayment();
          }
        }
      };
      
      // Execute AUTOMATIC recovery immediately
      attemptAutomaticRecovery();
    }
  }, []);

  // Regular payment callback processing (for web and when URL has response data)
  useEffect(() => {
    // Skip web flow for mobile devices - they have their own handling logic above
    if (Capacitor.isNativePlatform()) {
      return; // Skip web flow entirely for mobile devices
    }
    
    if (paymentResult || processing === false) {
      return; // Skip if already processed
    }

    // Only process if we're not waiting for mobile payment completion
    const pendingPayment = localStorage.getItem('pendingPayment');
    
    if (pendingPayment && window.location.pathname === '/payment-callback') {
      // Let mobile handling in the above useEffect take care of this
      return;
    }

    const handlePaymentResponse = async () => {
      const params = new URLSearchParams(window.location.search);
      
      if (params.size === 0) {
        setError('No payment response received');
        setPaymentResult('failed');
        setProcessing(false);
        return;
      }

      const responseData = Object.fromEntries(params.entries());
      
      // Transform URL parameters into expected format for display
      const paymentStatus = responseData.status || responseData.order_status;
      const displayData = {
        order_status: paymentStatus,
        order_id: responseData.orderId || responseData.order_id || 'N/A',
        tracking_id: responseData.transactionId || responseData.tracking_id || 'N/A',
        amount: responseData.amount || 'N/A',
        status_message: responseData.message || responseData.status_message || (paymentStatus === 'Success' ? 'Payment completed successfully' : 'Payment failed')
      };
      
      setPaymentDetails(displayData);

      // Check for success using either 'status' or 'order_status' parameter
      if (paymentStatus === 'Success' || paymentStatus === 'Shipped') {
        try {
          // Prepare payment update data with all required fields
          const updateData = {
            status: paymentStatus,
            orderId: responseData.orderId || responseData.order_id,
            paymentId: responseData.paymentId || responseData.payment_id,
            flatId: responseData.flatId || responseData.flat_id,
            buildingId: responseData.buildingId || responseData.building_id,
            transactionId: responseData.transactionId || responseData.tracking_id,
            amount: responseData.amount,
            message: responseData.message || responseData.status_message
          };
          
          // Only call updatePaymentRecord if we have the required IDs
          if (updateData.paymentId && updateData.flatId && updateData.buildingId) {
            await updatePaymentRecord(updateData);
            console.log('Web payment record updated successfully');
          } else {
            console.log('Skipping payment record update - missing required IDs (paymentId, flatId, buildingId)');
            console.log('Available data:', updateData);
          }
          
          // Set success state AFTER payment update completes to prevent race condition
          setPaymentResult('success');
        } catch (updateError) {
          console.error('Web payment update failed:', updateError);
          // Set success state even if update fails, but log the error
          setPaymentResult('success');
        }
      } else {
        setPaymentResult('failed');
        setError(responseData.failure_message || responseData.status_message || responseData.message || 'Payment failed');
      }
      setProcessing(false);
    };

    handlePaymentResponse();
  }, [processing, paymentResult]);

  const handleReturnToDashboard = () => {
    // Close popup if opened as popup
    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      window.close();
      return;
    }
    
    // Navigate to dashboard with a success parameter to trigger page refresh
    navigate('/?payment=success', { replace: true });
  };

  const handleTryAgain = () => {
    // Close popup if opened as popup
    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      window.close();
      return;
    }
    
    // Navigate back to dashboard
    navigate('/', { replace: true });
  };

  if (processing) {
    return (
      <>
        <MobilePaymentStatus show={showMobileDebug} />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 500,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3, color: '#667eea' }} />
          <Typography variant="h5" gutterBottom>
            Processing Payment
          </Typography>
          <Typography color="text.secondary">
            Please wait while we verify your payment details...
          </Typography>
        </Paper>
      </Box>
      </>
    );
  }

  if (paymentResult === 'success') {
    return (
      <>
        <MobilePaymentStatus show={showMobileDebug} />
        <Box
          onTouchStart={handleScreenTap}
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 2
          }}
        >
        <Paper
          sx={{
            p: { xs: 2, sm: 4 },
            textAlign: 'center',
            maxWidth: 600,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            mx: 2
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: { xs: 60, sm: 80 }, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography 
            variant="h4" 
            gutterBottom 
            color="success.main"
            sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            Payment Successful! 🎉
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Your payment has been processed successfully.
          </Typography>
          
          <Card sx={{ mb: 3, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Order ID:</strong>
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {paymentDetails.order_id || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography><strong>Amount:</strong></Typography>
                  <Typography variant="h6" color="success.main">₹{paymentDetails.amount || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Transaction ID:</strong>
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {paymentDetails.tracking_id || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography><strong>Status:</strong></Typography>
                  <Chip 
                    label={paymentDetails.order_status || 'Success'} 
                    color="success" 
                    size="small" 
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Button
            variant="contained"
            onClick={handleReturnToDashboard}
            fullWidth
            sx={{
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5a67d8' },
              px: 4,
              py: 1.5,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              maxWidth: { xs: '100%', sm: '300px' }
            }}
          >
            RETURN TO DASHBOARD
          </Button>
        </Paper>
      </Box>
      </>
    );
  }

  // Error or failure state
  return (
    <>
      <MobilePaymentStatus show={showMobileDebug} />
      <Box
        onTouchStart={handleScreenTap}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2
        }}
      >
      <Paper
        sx={{
          p: { xs: 2, sm: 4 },
          textAlign: 'center',
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          mx: 2
        }}
      >
        <ErrorIcon 
          sx={{ 
            fontSize: { xs: 60, sm: 80 }, 
            color: 'error.main', 
            mb: 2 
          }} 
        />
        <Typography 
          variant="h4" 
          gutterBottom 
          color="error.main"
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          Payment Failed
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {error || 'Your payment could not be processed. Please try again.'}
        </Typography>
        
        {paymentDetails && Object.keys(paymentDetails).length > 0 && (
          <Card sx={{ mb: 3, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Order ID:</strong>
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordBreak: 'break-all',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {paymentDetails.order_id || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography><strong>Status:</strong></Typography>
                  <Chip 
                    label={paymentDetails.order_status || 'Failed'} 
                    color="error" 
                    size="small" 
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Message:</strong>
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {paymentDetails.status_message || 'Payment failed'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        
        <Button
          variant="contained"
          onClick={handleTryAgain}
          fullWidth
          sx={{
            bgcolor: '#667eea',
            '&:hover': { bgcolor: '#5a67d8' },
            px: 4,
            py: 1.5,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            maxWidth: { xs: '100%', sm: '300px' }
          }}
        >
          RETURN TO DASHBOARD
        </Button>
      </Paper>
    </Box>
    </>
  );
};

export default PaymentCallback;
